'use client';

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { DownloadIcon, PackageIcon, RotateCwIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface LayoutMosaicToolProps {
  images: string[];
}

interface LayoutTemplate {
  name: string;
  cells: { x: number; y: number; w: number; h: number }[];
}

// 布局模板定义 - 使用绝对坐标系统
const layoutTemplates: { name: string; cols: number; rows: number; cells: { id: string; x: number; y: number; w: number; h: number }[] }[] = [
  {
    name: '2x2 网格',
    cols: 2,
    rows: 2,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 1 },
      { id: '2', x: 1, y: 0, w: 1, h: 1 },
      { id: '3', x: 0, y: 1, w: 1, h: 1 },
      { id: '4', x: 1, y: 1, w: 1, h: 1 },
    ],
  },
  {
    name: '1大+2小',
    cols: 2,
    rows: 2,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 2 },
      { id: '2', x: 1, y: 0, w: 1, h: 1 },
      { id: '3', x: 1, y: 1, w: 1, h: 1 },
    ],
  },
  {
    name: '2大+1小',
    cols: 2,
    rows: 2,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 1 },
      { id: '2', x: 1, y: 0, w: 1, h: 1 },
      { id: '3', x: 0, y: 1, w: 2, h: 1 },
    ],
  },
  {
    name: '3x3 网格',
    cols: 3,
    rows: 3,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 1 }, { id: '2', x: 1, y: 0, w: 1, h: 1 }, { id: '3', x: 2, y: 0, w: 1, h: 1 },
      { id: '4', x: 0, y: 1, w: 1, h: 1 }, { id: '5', x: 1, y: 1, w: 1, h: 1 }, { id: '6', x: 2, y: 1, w: 1, h: 1 },
      { id: '7', x: 0, y: 2, w: 1, h: 1 }, { id: '8', x: 1, y: 2, w: 1, h: 1 }, { id: '9', x: 2, y: 2, w: 1, h: 1 },
    ],
  },
  {
    name: '横排2张',
    cols: 2,
    rows: 1,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 1 },
      { id: '2', x: 1, y: 0, w: 1, h: 1 },
    ],
  },
  {
    name: '横排3张',
    cols: 3,
    rows: 1,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 1 },
      { id: '2', x: 1, y: 0, w: 1, h: 1 },
      { id: '3', x: 2, y: 0, w: 1, h: 1 },
    ],
  },
  {
    name: '竖排2张',
    cols: 1,
    rows: 2,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 1 },
      { id: '2', x: 0, y: 1, w: 1, h: 1 },
    ],
  },
  {
    name: '竖排3张',
    cols: 1,
    rows: 3,
    cells: [
      { id: '1', x: 0, y: 0, w: 1, h: 1 },
      { id: '2', x: 0, y: 1, w: 1, h: 1 },
      { id: '3', x: 0, y: 2, w: 1, h: 1 },
    ],
  },
];

export function LayoutMosaicTool({ images }: LayoutMosaicToolProps) {
  const [selectedTemplate, setSelectedTemplate] = useState(layoutTemplates[0].name);
  const [cellSize, setCellSize] = useState([300]);
  const [gap, setGap] = useState([8]);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [outputFormat, setOutputFormat] = useState<'png' | 'jpeg' | 'webp'>('png');
  const [rotations, setRotations] = useState<{ [key: string]: number }>({});
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [loadedImages, setLoadedImages] = useState<{ [key: number]: HTMLImageElement }>({});

  const template = useMemo(() => {
    return layoutTemplates.find(t => t.name === selectedTemplate) || layoutTemplates[0];
  }, [selectedTemplate]);

  // 预加载所有图片
  useEffect(() => {
    const loadImages = async () => {
      const loaded: { [key: number]: HTMLImageElement } = {};
      for (let i = 0; i < images.length; i++) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = images[i];
        });
        loaded[i] = img;
      }
      setLoadedImages(loaded);
    };
    loadImages();
  }, [images]);

  // 计算布局尺寸
  const layoutSize = useMemo(() => {
    const width = template.cols * cellSize[0] + (template.cols - 1) * gap[0];
    const height = template.rows * cellSize[0] + (template.rows - 1) * gap[0];
    return { width, height };
  }, [template, cellSize, gap]);

  // 计算预览缩放
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;
      
      const containerWidth = containerRef.current.clientWidth - 32;
      const maxHeight = 450;
      
      const scaleX = containerWidth / layoutSize.width;
      const scaleY = maxHeight / layoutSize.height;
      const scale = Math.min(scaleX, scaleY, 1);
      
      setPreviewScale(scale);
    };
    
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [layoutSize]);

  // 绘制预览
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || Object.keys(loadedImages).length === 0) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = previewScale;
    canvas.width = layoutSize.width * scale;
    canvas.height = layoutSize.height * scale;

    // 绘制背景
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 绘制每个格子
    template.cells.forEach((cell, index) => {
      const imgIndex = index % images.length;
      const img = loadedImages[imgIndex];
      const rotation = rotations[cell.id] || 0;
      
      // 计算格子位置和大小
      const x = (cell.x * (cellSize[0] + gap[0])) * scale;
      const y = (cell.y * (cellSize[0] + gap[0])) * scale;
      const w = (cell.w * cellSize[0] + (cell.w - 1) * gap[0]) * scale;
      const h = (cell.h * cellSize[0] + (cell.h - 1) * gap[0]) * scale;
      
      if (img && img.complete && img.naturalWidth > 0) {
        ctx.save();
        
        // 应用旋转
        if (rotation !== 0) {
          const centerX = x + w / 2;
          const centerY = y + h / 2;
          ctx.translate(centerX, centerY);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.translate(-centerX, -centerY);
        }
        
        // 绘制图片（cover模式 - 填满格子）
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const cellAspect = w / h;
        
        let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
        
        if (imgAspect > cellAspect) {
          // 图片更宽，按高度裁剪宽度
          sw = img.naturalHeight * cellAspect;
          sx = (img.naturalWidth - sw) / 2;
        } else {
          // 图片更高，按宽度裁剪高度
          sh = img.naturalWidth / cellAspect;
          sy = (img.naturalHeight - sh) / 2;
        }
        
        ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
        ctx.restore();
      } else {
        // 占位背景
        ctx.fillStyle = '#e5e5e5';
        ctx.fillRect(x, y, w, h);
        
        // 显示加载中
        ctx.fillStyle = '#999';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('加载中...', x + w / 2, y + h / 2);
      }
      
      // 绘制边框
      ctx.strokeStyle = '#ddd';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, h);
      
      // 绘制编号
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.beginPath();
      ctx.arc(x + 16, y + 16, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'white';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((index + 1).toString(), x + 16, y + 16);
    });
  }, [template, loadedImages, images, cellSize, gap, bgColor, previewScale, layoutSize, rotations]);

  // 生成并下载
  const handleGenerate = useCallback(async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = layoutSize.width;
    canvas.height = layoutSize.height;

    // 绘制背景
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 绘制每个格子
    for (let i = 0; i < template.cells.length; i++) {
      const cell = template.cells[i];
      const imgIndex = i % images.length;
      const img = loadedImages[imgIndex];
      const rotation = rotations[cell.id] || 0;
      
      // 计算格子位置和大小
      const x = cell.x * (cellSize[0] + gap[0]);
      const y = cell.y * (cellSize[0] + gap[0]);
      const w = cell.w * cellSize[0] + (cell.w - 1) * gap[0];
      const h = cell.h * cellSize[0] + (cell.h - 1) * gap[0];
      
      if (img && img.complete && img.naturalWidth > 0) {
        ctx.save();
        
        // 应用旋转
        if (rotation !== 0) {
          const centerX = x + w / 2;
          const centerY = y + h / 2;
          ctx.translate(centerX, centerY);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.translate(-centerX, -centerY);
        }
        
        // 绘制图片
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const cellAspect = w / h;
        
        let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
        
        if (imgAspect > cellAspect) {
          sw = img.naturalHeight * cellAspect;
          sx = (img.naturalWidth - sw) / 2;
        } else {
          sh = img.naturalWidth / cellAspect;
          sy = (img.naturalHeight - sh) / 2;
        }
        
        ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
        ctx.restore();
      }
    }

    // 下载
    const format = outputFormat === 'jpeg' ? 'image/jpeg' : outputFormat === 'webp' ? 'image/webp' : 'image/png';
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `layout-mosaic-${Date.now()}.${outputFormat}`;
          link.click();
          URL.revokeObjectURL(url);
        }
      },
      format,
      0.92
    );
  }, [template, loadedImages, images, cellSize, gap, bgColor, layoutSize, rotations, outputFormat]);

  // 下载原图 ZIP
  const handleDownloadOriginalsAsZip = async () => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    for (let i = 0; i < images.length; i++) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = images[i];
      });
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      
      const format = outputFormat === 'jpeg' ? 'image/jpeg' : outputFormat === 'webp' ? 'image/webp' : 'image/png';
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, format, 0.92);
      });
      
      if (blob) {
        zip.file(`original-${i + 1}.${outputFormat}`, blob);
      }
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `original-images-${Date.now()}.zip`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleRotation = (cellId: string) => {
    setRotations(prev => ({
      ...prev,
      [cellId]: ((prev[cellId] || 0) + 90) % 360,
    }));
  };

  return (
    <div className="space-y-6">
      {/* 工具栏 */}
      <div className="flex flex-wrap items-center gap-6 rounded-lg border bg-muted/50 p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">布局模板</Label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {layoutTemplates.map((t, index) => (
                  <SelectItem key={index} value={t.name}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">格子大小</Label>
            <Slider
              value={cellSize}
              onValueChange={setCellSize}
              max={600}
              min={100}
              step={10}
              className="w-40"
            />
            <span className="w-16 text-sm">{cellSize[0]}px</span>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">间距</Label>
            <Slider
              value={gap}
              onValueChange={setGap}
              max={30}
              min={0}
              step={1}
              className="w-32"
            />
            <span className="w-12 text-sm">{gap[0]}px</span>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">背景</Label>
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="h-8 w-12 cursor-pointer rounded border"
            />
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">格式</Label>
            <Select value={outputFormat} onValueChange={(val) => setOutputFormat(val as 'png' | 'jpeg' | 'webp')}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="png">PNG</SelectItem>
                <SelectItem value="jpeg">JPEG</SelectItem>
                <SelectItem value="webp">WebP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* 预览和设置 */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* 图片分配 */}
        <div className="space-y-4">
          <h3 className="font-medium">图片分配 ({template.cells.length} 个格子)</h3>
          <p className="text-sm text-muted-foreground">
            点击旋转按钮可旋转单张图片
          </p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {template.cells.map((cell, index) => (
              <div
                key={cell.id}
                className="flex items-center gap-2 rounded-lg border p-2"
              >
                <span className="w-6 text-sm text-muted-foreground text-center">
                  {index + 1}
                </span>
                <div 
                  className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded border"
                  style={{ backgroundColor: bgColor }}
                >
                  <img
                    src={images[index % images.length]}
                    alt=""
                    className="h-full w-full object-cover"
                    style={{
                      transform: `rotate(${rotations[cell.id] || 0}deg)`,
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
                <span className="flex-1 truncate text-sm">
                  图片 {index % images.length + 1}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => toggleRotation(cell.id)}
                >
                  <RotateCwIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* 预览 */}
        <div className="space-y-4">
          <h3 className="font-medium">预览</h3>
          <div 
            ref={containerRef}
            className="flex items-center justify-center rounded-lg border bg-muted p-4 overflow-auto"
            style={{ minHeight: '400px', maxHeight: '500px' }}
          >
            <canvas 
              ref={canvasRef} 
              className="max-w-full shadow-lg"
              style={{ borderRadius: '4px' }}
            />
          </div>
          <div className="text-center text-sm text-muted-foreground">
            输出尺寸: {layoutSize.width} x {layoutSize.height} px
          </div>
        </div>

        {/* 操作 */}
        <div className="space-y-4">
          <h3 className="font-medium">设置说明</h3>
          <div className="rounded-lg border bg-card p-4">
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• 选择布局模板确定图片排列方式</li>
              <li>• 调整格子大小和间距</li>
              <li>• 点击旋转按钮可以旋转单张图片</li>
              <li>• 图片会自动填满每个格子</li>
              <li>• 点击下载生成拼图</li>
            </ul>
          </div>

          <Button className="w-full" size="lg" onClick={handleGenerate}>
            <DownloadIcon className="mr-2 h-4 w-4" />
            下载拼图
          </Button>
          
          <Button
            variant="outline"
            className="w-full"
            onClick={handleDownloadOriginalsAsZip}
          >
            <PackageIcon className="mr-2 h-4 w-4" />
            下载原图 ZIP ({images.length} 张)
          </Button>
        </div>
      </div>
    </div>
  );
}
